import { useEffect } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { Form, Input, Button, Typography, Alert, Space } from 'antd'
import { UserOutlined, LockOutlined } from '@ant-design/icons'
import { useMutation } from '@tanstack/react-query'
import { useAuthStore } from '@/features/auth/store'
import { authApi } from '@/features/auth/api'
import type { LoginRequest } from '@/shared/types/auth'

const { Title, Text } = Typography

export default function LoginPage() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const [form] = Form.useForm<LoginRequest>()

  const { accessToken, setTokens, setUser } = useAuthStore()
  const sessionExpired = searchParams.get('reason') === 'session_expired'

  useEffect(() => {
    if (accessToken) {
      try {
        const payload = JSON.parse(atob(accessToken.split('.')[1]))
        if (Date.now() < payload.exp * 1000) { navigate('/', { replace: true }); return }
      } catch { /* invalid token */ }
    }
  }, [accessToken, navigate])

  const loginMutation = useMutation({
    mutationFn: async (values: LoginRequest) => authApi.login(values),
    onSuccess: async (tokens) => {
      setTokens(tokens.access_token, tokens.refresh_token)
      try {
        const me = await authApi.me()
        setUser(me)
      } catch {
        useAuthStore.getState().clearAuth(); return
      }
      navigate('/', { replace: true })
    },
  })

  const errorMessage = loginMutation.error
    ? (() => {
        const err = loginMutation.error as { response?: { status?: number } }
        if (err.response?.status === 401) return 'Invalid username or password.'
        if (err.response?.status === 403) return 'Your account is inactive. Contact an administrator.'
        return 'Login failed. Please try again.'
      })()
    : null

  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      background: 'linear-gradient(135deg, #001529 0%, #003a6e 60%, #005fa3 100%)',
      padding: '24px 16px',
    }}>

      {/* Card */}
      <div style={{
        width: '100%',
        maxWidth: 400,
        background: '#fff',
        borderRadius: 16,
        padding: '36px 32px',
        boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
      }}>
        <Space direction="vertical" size={20} style={{ width: '100%' }}>

          {/* Logo + Branding */}
          <div style={{ textAlign: 'center' }}>
            <div style={{
              width: 56, height: 56,
              borderRadius: 14,
              background: 'linear-gradient(135deg, #4096ff, #0958d9)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              margin: '0 auto 12px',
              boxShadow: '0 4px 16px rgba(9,88,217,0.35)',
            }}>
              <span style={{ color: '#fff', fontWeight: 800, fontSize: 22 }}>S</span>
            </div>
            <Title level={3} style={{ margin: 0, color: '#1a1a2e' }}>
              Spinning Mill ERP
            </Title>
            <Text type="secondary" style={{ fontSize: 13 }}>
              Sign in to your account
            </Text>
          </div>

          {/* Alerts */}
          {sessionExpired && (
            <Alert
              type="warning"
              message="Session expired"
              description="Your session has expired. Please sign in again."
              showIcon closable
            />
          )}
          {errorMessage && (
            <Alert type="error" message={errorMessage} showIcon />
          )}

          {/* Form */}
          <Form
            form={form}
            layout="vertical"
            onFinish={v => loginMutation.mutate(v)}
            autoComplete="off"
            size="large"
            style={{ marginBottom: 0 }}
          >
            <Form.Item
              name="username"
              style={{ marginBottom: 14 }}
              rules={[{ required: true, message: 'Enter your username or email' }]}
            >
              <Input
                prefix={<UserOutlined style={{ color: '#0958d9' }} />}
                placeholder="Username or email"
                style={{ borderRadius: 10, height: 46 }}
                autoCapitalize="none"
                autoCorrect="off"
              />
            </Form.Item>

            <Form.Item
              name="password"
              style={{ marginBottom: 20 }}
              rules={[{ required: true, message: 'Enter your password' }]}
            >
              <Input.Password
                prefix={<LockOutlined style={{ color: '#0958d9' }} />}
                placeholder="Password"
                style={{ borderRadius: 10, height: 46 }}
              />
            </Form.Item>

            <Button
              type="primary"
              htmlType="submit"
              block
              loading={loginMutation.isPending}
              style={{
                height: 48,
                borderRadius: 10,
                fontSize: 15,
                fontWeight: 600,
                background: 'linear-gradient(135deg, #4096ff, #0958d9)',
                border: 'none',
                boxShadow: '0 4px 14px rgba(9,88,217,0.4)',
              }}
            >
              Sign In
            </Button>
          </Form>
        </Space>
      </div>

      {/* Footer */}
      <Text style={{ color: 'rgba(255,255,255,0.4)', fontSize: 11, marginTop: 24 }}>
        © 2026 Spinning Mill ERP. All rights reserved.
      </Text>
    </div>
  )
}
